import {
  AssetWatchLogo,
  Divider,
  type IconName,
  Sidebar,
  SidebarFooter,
  SidebarGroup,
  SidebarItem,
} from "@assetwatch/ui";
import { Link } from "@tanstack/react-router";
import {
  type WebNavigationModule,
  webNavigationModulesForRole,
} from "../../lib/access";
import type { AssetTreeSearch } from "../../lib/assetTreeSearch";
import { asFacilityId } from "../../lib/workIds";
import { getWorkScopeSearch } from "../../lib/workSearch";
import { useUnreadWorkOrderCountQuery } from "../../queries/useUnreadWorkOrderCountQuery";
import { useUnreadWorkRequestCountQuery } from "../../queries/useUnreadWorkRequestCountQuery";
import { useWorkScope } from "../WorkScopeProvider";
import { useRetainedAssetsSearch } from "./useRetainedListState";
import { HeaderActions } from "./WorkHeader";

type NavigationItem = {
  itemId: string;
  label: string;
  icon: IconName;
  to:
    | "/work-orders"
    | "/requests"
    | "/assets"
    | "/procedures"
    | "/team-users"
    | "/settings/general";
  /** Route prefix that keeps this destination selected. */
  activePrefix: string;
};

const navigationItems: Readonly<Record<WebNavigationModule, NavigationItem>> = {
  WORK_ORDERS: {
    itemId: "work-orders",
    label: "Work Orders",
    icon: "workorder",
    to: "/work-orders",
    activePrefix: "/work-orders",
  },
  REQUESTS: {
    itemId: "requests",
    label: "Requests",
    icon: "checklist",
    to: "/requests",
    activePrefix: "/requests",
  },
  ASSETS: {
    itemId: "assets",
    label: "Assets",
    icon: "asset",
    to: "/assets",
    activePrefix: "/assets",
  },
  PROCEDURES: {
    itemId: "procedures",
    label: "Procedures",
    icon: "list",
    to: "/procedures",
    activePrefix: "/procedures",
  },
  TEAMS_USERS: {
    itemId: "team-users",
    label: "Teams / Users",
    icon: "user",
    to: "/team-users",
    activePrefix: "/team-users",
  },
  SETTINGS: {
    itemId: "settings",
    label: "Settings",
    icon: "settings-cog-solid",
    to: "/settings/general",
    activePrefix: "/settings",
  },
};

/** Destinations listed under the Manage header; the rest sit above the divider. */
const manageGroupModules: readonly WebNavigationModule[] = [
  "ASSETS",
  "TEAMS_USERS",
  "PROCEDURES",
];

function isActive(item: NavigationItem, pathname: string) {
  return (
    pathname === item.activePrefix ||
    pathname.startsWith(`${item.activePrefix}/`)
  );
}

function renderNavigationLink(
  item: NavigationItem,
  search: ReturnType<typeof getWorkScopeSearch>,
  pathname: string,
  assetsSearch: AssetTreeSearch,
) {
  const onClick =
    pathname === item.to
      ? (event: React.MouseEvent<HTMLAnchorElement>) => event.preventDefault()
      : undefined;
  switch (item.to) {
    case "/work-orders":
      return <Link onClick={onClick} search={search} to="/work-orders" />;
    case "/requests":
      return <Link onClick={onClick} search={search} to="/requests" />;
    case "/assets":
      return (
        <Link
          onClick={onClick}
          search={{ ...search, ...(pathname === item.to ? {} : assetsSearch) }}
          to="/assets"
        />
      );
    case "/procedures":
      return <Link onClick={onClick} search={search} to="/procedures" />;
    case "/team-users":
      return <Link onClick={onClick} search={search} to="/team-users" />;
    case "/settings/general":
      return <Link onClick={onClick} search={search} to="/settings/general" />;
  }
}

type WorkSidebarProps = {
  pathname?: string;
};

export function WorkSidebar({ pathname = "/" }: WorkSidebarProps) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const retainedAssetsSearch = useRetainedAssetsSearch();
  const modules = selectedFacility
    ? webNavigationModulesForRole(selectedFacility.role)
    : [];
  const { data: unreadRequestCount } = useUnreadWorkRequestCountQuery({
    enabled: modules.includes("REQUESTS"),
  });
  const { data: unreadWorkOrderCount } = useUnreadWorkOrderCountQuery({
    enabled: modules.includes("WORK_ORDERS"),
  });
  const search =
    selectedCustomer && selectedFacility
      ? getWorkScopeSearch(
          {
            id: selectedCustomer.id,
            facilities: { length: selectedCustomer.facilities.length },
          },
          { id: asFacilityId(selectedFacility.id) },
        )
      : undefined;

  const manageModules = modules.filter((module) =>
    manageGroupModules.includes(module),
  );
  const topModules = modules.filter(
    (module) => module !== "SETTINGS" && !manageModules.includes(module),
  );

  function renderItem(module: WebNavigationModule) {
    const item = navigationItems[module];
    const render = search
      ? renderNavigationLink(item, search, pathname, retainedAssetsSearch)
      : undefined;
    const badge =
      module === "REQUESTS" && unreadRequestCount && unreadRequestCount > 0
        ? {
            count: unreadRequestCount,
            label: `${unreadRequestCount} unread ${unreadRequestCount === 1 ? "request" : "requests"}`,
            tone: "activity" as const,
          }
        : module === "WORK_ORDERS" &&
            unreadWorkOrderCount &&
            unreadWorkOrderCount > 0
          ? {
              count: unreadWorkOrderCount,
              label: `${unreadWorkOrderCount} unread ${unreadWorkOrderCount === 1 ? "work order" : "work orders"}`,
              tone: "activity" as const,
            }
          : undefined;

    return (
      <SidebarItem
        badge={badge}
        href={render ? undefined : "#"}
        itemId={item.itemId}
        key={item.itemId}
        label={item.label}
        leading={{ type: "icon", name: item.icon }}
        render={render}
        selected={isActive(item, pathname)}
      />
    );
  }

  return (
    <Sidebar
      aria-label="Work navigation"
      mobileHeaderActions={<HeaderActions />}
      mobileHeaderLeading={<AssetWatchLogo aria-label="AssetWatch" />}
    >
      {topModules.map(renderItem)}
      {topModules.length > 0 && manageModules.length > 0 && <Divider />}
      {manageModules.length > 0 && (
        /* @ds-deviation: the DS SidebarGroup header does not support uppercase, so the wrapper uppercases it with CSS only (the label text stays "Manage") — promote an uppercase group-header variant into the design system */
        <div
          className="[&>[data-sidebar-group]>div:first-child]:uppercase"
          data-slot="manage-group"
        >
          <SidebarGroup collapsible={false} itemId="manage" label="Manage">
            {manageModules.map(renderItem)}
          </SidebarGroup>
        </div>
      )}
      {modules.includes("SETTINGS") && (
        <SidebarFooter>{renderItem("SETTINGS")}</SidebarFooter>
      )}
    </Sidebar>
  );
}
